import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import MyPlanningPage from "./pages/MyPlanningPage";
import EquipmentPage from "./pages/EquipmentPage";
import DirectoryPage from "./pages/DirectoryPage";
import MyProfilePage from "./pages/MyProfilePage";
import EventsPage from "./pages/EventsPage";
import TasksPage from "./pages/TasksPage";
import DailyRecapsPage from "./pages/DailyRecapsPage";
import PayrollAdminPage from "./pages/PayrollAdminPage";
import MyPayslipsPage from "./pages/MyPayslipsPage";
import CustomersPage from "./pages/CustomersPage";
import CatalogPage from "./pages/CatalogPage";
import DocumentsPage from "./pages/DocumentsPage";
import DocumentEditorPage from "./pages/DocumentEditorPage";
import SettingsPage from "./pages/SettingsPage";
import UsersPage from "./pages/UsersPage";
import ReportsPage from "./pages/ReportsPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import RecurringInvoicesPage from "./pages/RecurringInvoicesPage";
import NotificationsPage from "./pages/NotificationsPage";
import CommunicationPage from "./pages/CommunicationPage";
import ContentCalendarPage from "./pages/ContentCalendarPage";
import CommunicationStatsPage from "./pages/CommunicationStatsPage";
import EmployeesPage from "./pages/EmployeesPage";
import EmployeeDetailPage from "./pages/EmployeeDetailPage";
import PlanningPage from "./pages/PlanningPage";
import AbsencesPage from "./pages/AbsencesPage";
import ExpensesPage from "./pages/ExpensesPage";
import Layout from "./components/Layout";
import { TabRoute } from "./components/TabRoute";

const CalendarRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/calendrier-diffusion${search}`} replace />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, mustChangePassword } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Force password change for new users
  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<DashboardPage />} />
                    <Route path="/mon-planning" element={<MyPlanningPage />} />
                    <Route path="/materiel" element={<EquipmentPage />} />
                    <Route path="/repertoire" element={<DirectoryPage />} />
                    <Route path="/mes-infos" element={<MyProfilePage />} />
                    <Route path="/evenements" element={<EventsPage />} />
                    <Route path="/taches" element={<TasksPage />} />
                    <Route path="/taches/recap" element={<DailyRecapsPage />} />
                    <Route path="/bulletins-salaire" element={<PayrollAdminPage />} />
                    <Route path="/mes-bulletins" element={<MyPayslipsPage />} />
                    <Route path="/notes-de-frais" element={<TabRoute tab="expenses"><ExpensesPage /></TabRoute>} />
                    <Route path="/clients" element={<CustomersPage />} />
                    <Route path="/catalogue" element={<CatalogPage />} />
                    <Route path="/documents" element={<DocumentsPage />} />
                    <Route path="/documents/:type" element={<DocumentsPage />} />
                    <Route path="/document/new/:type" element={<DocumentEditorPage />} />
                    <Route path="/document/:id" element={<DocumentEditorPage />} />
                    <Route path="/rapports" element={<ReportsPage />} />
                    <Route path="/reports/client/:customerId" element={<ClientDetailPage />} />
                    <Route path="/reports/product/:productName" element={<ProductDetailPage />} />
                    <Route path="/recurring" element={<RecurringInvoicesPage />} />
                    <Route path="/communication" element={<TabRoute tab="communication"><CommunicationPage /></TabRoute>} />
                    <Route path="/communication/calendrier" element={<CalendarRedirect />} />
                    <Route path="/communication/statistiques" element={<TabRoute tab="communication"><CommunicationStatsPage /></TabRoute>} />
                    <Route path="/calendrier-diffusion" element={<TabRoute tab="content_calendar"><ContentCalendarPage /></TabRoute>} />
                    <Route path="/calendrier-diffusion/statistiques" element={<TabRoute tab="content_calendar"><CommunicationStatsPage key="calendar-stats" calendarOnly /></TabRoute>} />
                    <Route path="/employes" element={<EmployeesPage />} />
                    <Route path="/employes/planning" element={<PlanningPage />} />
                    <Route path="/employes/absences" element={<AbsencesPage />} />
                    <Route path="/employes/:id" element={<EmployeeDetailPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/reglages" element={<SettingsPage />} />
                    <Route path="/utilisateurs" element={<UsersPage />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
        <Toaster position="top-right" duration={3000} />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
